import { clsx } from 'clsx'
import { motion, type HTMLMotionProps } from 'framer-motion'
import type { ReactNode, HTMLAttributes } from 'react'

interface CardProps extends HTMLMotionProps<'div'> {
  children: ReactNode
  hover?: boolean
  selected?: boolean
  gradient?: boolean
}

export const Card = ({
  children,
  hover = false,
  selected = false,
  gradient = false,
  className,
  ...props
}: CardProps) => {
  return (
    <motion.div
      whileHover={hover ? { y: -2, scale: 1.02 } : {}}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className={clsx(
        'bg-white dark:bg-night-800/90 rounded-xl shadow-sm dark:shadow-xl border border-gray-200 dark:border-night-750/35 transition-all duration-200',
        {
          'cursor-pointer': hover,
          'border-primary-500 shadow-lg shadow-primary-500/20': selected,
          'bg-gradient-to-br from-white to-gray-50 dark:from-night-825/95 dark:via-night-800/80 dark:to-night-900/85': gradient,
        },
        className
      )}
      {...props}
    >
      {children}
    </motion.div>
  )
}

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const CardHeader = ({ children, className, ...props }: CardHeaderProps) => (
  <div className={clsx('p-6 pb-4', className)} {...props}>
    {children}
  </div>
)

interface CardContentProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const CardContent = ({ children, className, ...props }: CardContentProps) => (
  <div className={clsx('p-6 pt-0', className)} {...props}>
    {children}
  </div>
)

interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const CardFooter = ({ children, className, ...props }: CardFooterProps) => (
  <div className={clsx('p-6 pt-4 border-t border-gray-100 dark:border-night-750/40', className)} {...props}>
    {children}
  </div>
)
